/**
 * The alert routes of the route table (`routes.ts` spreads these into
 * `ROUTES`): phase 7b item 4's tenant-facing alerts (`alerts.ts`) and item 3's
 * onboarding progress (`onboarding.ts`). Cloud only, like the sites table.
 *
 * Reading alerts needs `rundown.read` on the alert's site or studio (the
 * handler filters: an operator scoped to one studio sees that studio's
 * alerts, and its site's node alerts only with a grant on the site); the
 * settings, channels, test sends and onboarding are tenant administration.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import {
  acknowledgeAlertRequestSchema,
  alertChannelPathSchema,
  alertResponseSchema,
  alertChannelResponseSchema,
  alertSettingsResponseSchema,
  createAlertChannelRequestSchema,
  listAlertHistoryQuerySchema,
  listAlertHistoryResponseSchema,
  listAlertsQuerySchema,
  listAlertsResponseSchema,
  testAlertRequestSchema,
  testAlertResponseSchema,
  updateAlertChannelRequestSchema,
  updateAlertSettingsRequestSchema,
} from './alerts.js';
import { onboardingResponseSchema, updateOnboardingRequestSchema } from './onboarding.js';
import type { RouteDef } from './routes.js';

export const ALERTS_ROUTES = {
  listAlerts: {
    id: 'listAlerts',
    method: 'GET',
    path: '/alerts',
    tag: 'alerts',
    summary: 'List alerts',
    description:
      'The tenant’s alerts the caller may see (`rundown.read` on the alert’s site or studio), most severe and newest first: by default the conditions that hold now (a node offline during a live production, a standby missing, a certificate expiring, clock drift, a device disconnected on air — PLAN §11.1), with the active counts per severity for the top bar’s badge.',
    params: null,
    query: listAlertsQuerySchema,
    body: null,
    response: listAlertsResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request'],
  },
  listAlertHistory: {
    id: 'listAlertHistory',
    method: 'GET',
    path: '/alerts/history',
    tag: 'alerts',
    summary: 'The alert history',
    description:
      'Every raise, resolve and event occurrence the caller may see, newest first, in pages (kept 90 days), each with the channels it was sent to — empty when the cooldown, the type’s rule or the lack of a channel meant nobody was notified.',
    params: null,
    query: listAlertHistoryQuerySchema,
    body: null,
    response: listAlertHistoryResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request'],
  },
  acknowledgeAlert: {
    id: 'acknowledgeAlert',
    method: 'POST',
    path: '/alerts/acknowledge',
    tag: 'alerts',
    summary: 'Acknowledge an alert for everyone',
    description:
      'Marks an active alert as seen and being handled, for everyone: the operator screens’ alert bars hide it, the alerts page shows who acknowledged it and when, and the history records it (an `acknowledged` entry naming the caller — the audit). It covers the current episode only: once the condition resolves and is raised again, the alert is unacknowledged. Notifications are not affected. Needs `playout.take` on the alert’s site or studio. Acknowledging an alert someone already acknowledged answers it unchanged (their name and time stand). `not-found` for an alert the caller cannot see; `conflict` for one that is not active.',
    params: null,
    query: null,
    body: acknowledgeAlertRequestSchema,
    response: alertResponseSchema,
    successStatus: 200,
    permission: 'playout.take',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  getAlertSettings: {
    id: 'getAlertSettings',
    method: 'GET',
    path: '/alerts/settings',
    tag: 'alerts',
    summary: 'Get the notification settings',
    description:
      'The tenant’s alert rules (per type: on or off, and its severity), per-site overrides, the cooldown, and its channels. A channel’s URL and signing secret are never returned: `target` is a hint and `hasSecret` says one is stored. A tenant that never saved settings gets the defaults (every type on, at its default severity; a 15 minute cooldown; no channels).',
    params: null,
    query: null,
    body: null,
    response: alertSettingsResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  updateAlertSettings: {
    id: 'updateAlertSettings',
    method: 'PUT',
    path: '/alerts/settings',
    tag: 'alerts',
    summary: 'Save the notification settings',
    description:
      'Replaces the rules, the site overrides and the cooldown (the channels have their own routes), from the `version` named: another save since is `conflict` (D35: the settings screen offers overwrite or discard). `validation-failed` for a site override naming a site the tenant does not have.',
    params: null,
    query: null,
    body: updateAlertSettingsRequestSchema,
    response: alertSettingsResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  createAlertChannel: {
    id: 'createAlertChannel',
    method: 'POST',
    path: '/alerts/channels',
    tag: 'alerts',
    summary: 'Add a notification channel',
    description:
      'An email channel (to users and groups of the tenant, resolved to their addresses when an alert is sent), a Slack incoming webhook, or a generic webhook (an https URL; each delivery is a JSON body signed HMAC-SHA256 with the signing secret you choose, retried with backoff — docs/acp/alerts.md). The URL and secret go to Secrets Manager under the tenant’s path and are never returned. Supplying `channelId` makes a retry idempotent. `validation-failed` for a recipient not in the tenant, an unknown site, or more than 20 channels.',
    params: null,
    query: null,
    body: createAlertChannelRequestSchema,
    response: alertChannelResponseSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  updateAlertChannel: {
    id: 'updateAlertChannel',
    method: 'PATCH',
    path: '/alerts/channels/{channelId}',
    tag: 'alerts',
    summary: 'Change a notification channel',
    description: 'Changes its name, whether it is on, its lowest severity, its sites or recipients; a new `url` or `signingSecret` replaces the stored one. Its kind is fixed.',
    params: alertChannelPathSchema,
    query: null,
    body: updateAlertChannelRequestSchema,
    response: alertChannelResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  deleteAlertChannel: {
    id: 'deleteAlertChannel',
    method: 'DELETE',
    path: '/alerts/channels/{channelId}',
    tag: 'alerts',
    summary: 'Delete a notification channel',
    description: 'Deletes the channel and its stored URL and secret for good. Deliveries to it still queued are dropped.',
    params: alertChannelPathSchema,
    query: null,
    body: null,
    response: alertChannelResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  testAlert: {
    id: 'testAlert',
    method: 'POST',
    path: '/alerts/test',
    tag: 'alerts',
    summary: 'Send a test alert',
    description:
      'Sends a test notification to one channel, or to every enabled one, now and once (no retry), and answers how each went: `ok`, or what the receiver said. Nothing is written to the alert history. `not-found` for a channel the tenant does not have.',
    params: null,
    query: null,
    body: testAlertRequestSchema,
    response: testAlertResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  getOnboarding: {
    id: 'getOnboarding',
    method: 'GET',
    path: '/onboarding',
    tag: 'onboarding',
    summary: 'The onboarding wizard’s progress',
    description: 'Where the tenant’s first run through the wizard stands (phase 7b): its status, and each step’s — pending, done or skipped, with the resource it made. A tenant that never opened it is `not-started`.',
    params: null,
    query: null,
    body: null,
    response: onboardingResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  updateOnboarding: {
    id: 'updateOnboarding',
    method: 'PUT',
    path: '/onboarding',
    tag: 'onboarding',
    summary: 'Record onboarding progress',
    description: 'Records one step’s outcome and/or the wizard’s status (`in-progress`, `dismissed`, `completed`). Idempotent; a step’s resource, once recorded, is kept unless a new one is named.',
    params: null,
    query: null,
    body: updateOnboardingRequestSchema,
    response: onboardingResponseSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
} as const satisfies Record<string, RouteDef>;
